/**
 * Start for me — curated memory prompt library (V1).
 * Gentle, optional family storytelling prompts. No AI generation.
 */

function p(id, mainPrompt, followUps, suggestedChapter, tags, suitableFor = ['general'], tier = 'deeper', category = 'general') {
  return { id, mainPrompt, followUps, suggestedChapter, tags, suitableFor, tier, category };
}

export const MEMORY_PROMPTS = [
  p(
    'starter-never-forget',
    'What is one memory of this person you never want to forget?',
    ['What small detail still brings them to mind?', 'Who else shares this memory?'],
    'Family & Relationships',
    ['starter', 'family'],
    ['general', 'parent', 'child', 'grandparent', 'memorial'],
    'starter',
    'starter',
  ),
  p(
    'starter-loved-doing',
    'What did they love doing?',
    ['Where did they do it?', 'Who usually joined them?'],
    'Growing Up',
    ['starter', 'personality'],
    ['general', 'parent', 'child', 'grandparent', 'memorial'],
    'starter',
    'starter',
  ),
  p(
    'starter-phrase',
    'What phrase, joke or habit reminds you of them?',
    ['When do you hear it in your mind?', 'Who else says it now?'],
    'Family & Relationships',
    ['starter', 'personality', 'family sayings'],
    ['general', 'parent', 'child', 'grandparent', 'memorial'],
    'starter',
    'personality',
  ),
  p(
    'starter-place',
    'What place always makes you think of them?',
    ['What happened there?', 'What do you see or smell when you remember it?'],
    'The Early Years',
    ['starter', 'places', 'home'],
    ['general', 'parent', 'grandparent', 'memorial'],
    'starter',
    'places',
  ),
  p(
    'starter-lesson',
    'What lesson did they teach you?',
    ['How do you still live it?', 'When did you first understand it?'],
    'Legacy & Lessons',
    ['starter', 'lessons learned'],
    ['general', 'self', 'parent', 'grandparent'],
    'starter',
    'lessons',
  ),
  p(
    'starter-future',
    'What would you want future generations to know about them?',
    ['What quality should never be forgotten?', 'What story shows who they were?'],
    'Legacy & Lessons',
    ['starter', 'legacy'],
    ['general', 'parent', 'grandparent', 'memorial'],
    'starter',
    'legacy',
  ),
  p(
    'home-001',
    'What is the first place that still feels like home when you close your eyes?',
    ['Who was usually there?', 'What could you smell or hear?', 'What made that place special to you?'],
    'The Early Years',
    ['home', 'childhood', 'places'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'childhood-001',
    'What did afternoons look like when you were very young?',
    ['Who looked after you?', 'What did you love to play or do?', 'What small detail still makes you smile?'],
    'The Early Years',
    ['childhood', 'home'],
    ['general', 'self', 'parent', 'grandparent', 'child'],
  ),
  p(
    'parents-001',
    'What is one thing your parents did that you still carry with you?',
    ['Was it a habit, a phrase, or a way of showing love?', 'When do you notice it in yourself?', 'Who else remembers it?'],
    'Family Roots',
    ['parents', 'lessons learned', 'family sayings'],
    ['general', 'self', 'parent', 'child'],
  ),
  p(
    'school-001',
    'What do you remember most about your first school?',
    ['Who was a kind face there?', 'What felt hard or exciting?', 'What did you bring home and talk about?'],
    'Growing Up',
    ['school', 'childhood', 'friendships'],
    ['general', 'self', 'child'],
  ),
  p(
    'friendships-001',
    'Who was your first real friend — and what drew you together?',
    ['What did you do together?', 'Is there a moment that still makes you laugh?', 'Are you still in touch?'],
    'Growing Up',
    ['friendships', 'childhood'],
    ['general', 'self', 'child'],
  ),
  p(
    'work-001',
    'What was your first proper job, and what did it teach you?',
    ['How did you get it?', 'Who helped you along the way?', 'What skill or habit stayed with you?'],
    'Work & Purpose',
    ['work', 'turning points', 'lessons learned'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'love-001',
    'When did you first know you were in love — or deeply cared for someone?',
    ['What small gesture meant the most?', 'Where were you?', 'What would you want family to understand about that feeling?'],
    'Love & Partnership',
    ['love', 'marriage', 'turning points'],
    ['general', 'self', 'parent'],
  ),
  p(
    'marriage-001',
    'What is a quiet tradition you and your partner shared?',
    ['How did it start?', 'Who else noticed or joined in?', 'Would you like it remembered?'],
    'Love & Partnership',
    ['marriage', 'traditions', 'celebrations'],
    ['general', 'self', 'parent', 'memorial'],
  ),
  p(
    'children-001',
    'What moment with your children do you replay in your mind?',
    ['How old were they?', 'What were you doing together?', 'What did their face or voice do?'],
    'Family Life',
    ['children', 'proud moments'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'traditions-001',
    'What family tradition would you hate to see forgotten?',
    ['When does it happen each year?', 'Who keeps it going?', 'What does it smell, sound or taste like?'],
    'Family Life',
    ['traditions', 'celebrations', 'food'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'food-001',
    'What meal instantly takes you back to childhood?',
    ['Who cooked it?', 'Was it for everyday or special days?', 'Have you passed the recipe on?'],
    'Family Life',
    ['food', 'home', 'traditions'],
    ['general', 'self', 'parent', 'grandparent', 'child'],
  ),
  p(
    'music-001',
    'What song follows you through life — and why?',
    ['When did you first hear it?', 'Who sang or played it with you?', 'What memory sits behind it?'],
    'Moments & Meaning',
    ['music', 'people, places & things'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'places-001',
    'Which place outside home shaped who you became?',
    ['How often did you go there?', 'Who shared it with you?', 'What changed for you there?'],
    'People, Places & Things',
    ['places', 'turning points'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'turning-001',
    'What decision changed the direction of your life?',
    ['What were you choosing between?', 'Who advised you — or did you decide alone?', 'Looking back, what do you feel grateful for?'],
    'Turning Points',
    ['turning points', 'lessons learned'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'lessons-001',
    'What is one lesson you hope younger family members learn from your life?',
    ['How did you learn it — the easy way or the hard way?', 'Who taught you?', 'When do you still remind yourself of it?'],
    'Reflections',
    ['lessons learned', 'proud moments'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'proud-001',
    'What is something you are quietly proud of that not everyone knows?',
    ['What did it cost you in time or effort?', 'Who noticed?', 'Why does it still matter to you?'],
    'Reflections',
    ['proud moments', 'work'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'loss-001',
    'Who do you miss — and what is one gentle memory you return to?',
    ['What did they love?', 'What did they teach you without trying?', 'How do you honour them now?'],
    'Remembrance',
    ['loss', 'memorial'],
    ['memorial', 'general', 'parent', 'grandparent'],
  ),
  p(
    'pets-001',
    'Did a pet ever become part of the family story?',
    ['What was their personality?', 'What trouble or comfort did they bring?', 'Who cared for them most?'],
    'Family Life',
    ['pets', 'home', 'children'],
    ['general', 'self', 'child', 'parent'],
  ),
  p(
    'chosen-001',
    'Who is chosen family to you — someone not related by blood but deeply yours?',
    ['How did you meet?', 'What do they do that feels like home?', 'What would you want preserved about them?'],
    'Family & Community',
    ['chosen family', 'friendships'],
    ['general', 'self', 'parent', 'child'],
  ),
  p(
    'keepsakes-001',
    'What object in your home carries a story no one should lose?',
    ['Where did it come from?', 'Who gave it to you or passed it down?', 'What would you tell someone holding it for the first time?'],
    'People, Places & Things',
    ['favourite objects / keepsakes', 'people, places & things'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'sayings-001',
    'What phrase did someone in your family repeat — until everyone knew it?',
    ['Who said it most?', 'When did they say it?', 'Do you catch yourself saying it now?'],
    'Family Roots',
    ['family sayings', 'parents', 'humour'],
    ['general', 'self', 'parent', 'grandparent', 'child', 'memorial'],
  ),
  p(
    'celebrations-001',
    'What celebration in your family felt truly yours?',
    ['Who organised it?', 'What food, music or ritual made it special?', 'Who should hear about it years from now?'],
    'Family Life',
    ['celebrations', 'traditions'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'first-001',
    'What is one of your earliest memories — even if the details are fuzzy?',
    ['How old do you think you were?', 'Who appears in it?', 'What feeling does it leave you with?'],
    'First Memories',
    ['first memories', 'childhood'],
    ['general', 'self', 'parent', 'grandparent', 'child'],
  ),
  p(
    'grandparent-001',
    'What did a grandparent show you that no one else did?',
    ['Was it a skill, a place, or a way of listening?', 'When do you think of them?', 'What would you thank them for?'],
    'Family Roots',
    ['grandparent', 'parents', 'lessons learned'],
    ['general', 'self', 'child', 'parent', 'grandparent'],
  ),
  p(
    'sibling-001',
    'What is a story only you and a sibling share?',
    ['Were you partners or rivals back then?', 'What happened?', 'Do you still laugh or wince about it?'],
    'Growing Up',
    ['childhood', 'friendships', 'family'],
    ['general', 'self', 'child', 'parent'],
  ),
  p(
    'holiday-001',
    'Where did your family go on holidays — or day trips — that still matter?',
    ['Who packed what?', 'What went wrong or went perfectly?', 'What photo or smell brings it back?'],
    'Family Life',
    ['places', 'traditions', 'celebrations'],
    ['general', 'self', 'parent', 'child'],
  ),
  p(
    'craft-001',
    'What skill or craft did someone patiently teach you?',
    ['Whose hands guided yours?', 'Did you keep it up?', 'What would you show the next generation?'],
    'Family Roots',
    ['traditions', 'parents', 'proud moments'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'neighbourhood-001',
    'What was your street or neighbourhood like when you were growing up?',
    ['Who did you wave to each day?', 'Where were you allowed to roam?', 'What changed over the years?'],
    'The Early Years',
    ['home', 'places', 'childhood'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'teacher-001',
    'Was there a teacher who saw something in you before you did?',
    ['What did they encourage?', 'Do you remember their words?', 'Did you ever thank them?'],
    'Growing Up',
    ['school', 'turning points', 'lessons learned'],
    ['general', 'self', 'child'],
  ),
  p(
    'courage-001',
    'When did you have to be braver than you felt?',
    ['What were you afraid of?', 'Who stood beside you?', 'What helped you take the next step?'],
    'Turning Points',
    ['turning points', 'proud moments', 'lessons learned'],
    ['general', 'self', 'parent'],
  ),
  p(
    'humour-001',
    'What family story still makes everyone laugh at the table?',
    ['Who tells it best?', 'What actually happened?', 'Why is it worth keeping?'],
    'Family Life',
    ['celebrations', 'family sayings'],
    ['general', 'self', 'parent', 'grandparent', 'child', 'memorial'],
  ),
  p(
    'quiet-001',
    'Describe a quiet moment with someone you loved — nothing dramatic, just ordinary.',
    ['What were you doing side by side?', 'What sounds were in the background?', 'Why does that ordinary moment matter?'],
    'Reflections',
    ['love', 'home', 'memorial'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'garden-001',
    'Did anyone in your family grow things — flowers, vegetables, or a windowsill pot?',
    ['What did they grow?', 'What season do you associate with it?', 'Did you help?'],
    'Family Life',
    ['home', 'traditions', 'parents'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'faith-001',
    'Was faith, community or gathering important in your family — in any form?',
    ['Where did you come together?', 'What ritual or habit felt comforting?', 'What would you pass on without preaching?'],
    'Family Roots',
    ['traditions', 'celebrations'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'move-001',
    'Tell the story of a move — to a new house, town or country.',
    ['What did you leave behind?', 'What felt strange at first?', 'What eventually felt like home?'],
    'Turning Points',
    ['home', 'places', 'turning points'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'gift-001',
    'What is the most meaningful gift you ever received — not the most expensive?',
    ['Who gave it and when?', 'Why did it matter in that moment?', 'Do you still have it?'],
    'People, Places & Things',
    ['favourite objects / keepsakes', 'love'],
    ['general', 'self', 'parent', 'child', 'memorial'],
  ),
  p(
    'advice-001',
    'What advice did you ignore at first — then realise was right?',
    ['Who gave it?', 'What happened when you finally listened?', 'Would you give the same advice now?'],
    'Reflections',
    ['lessons learned', 'parents'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'weekend-001',
    'How did your family spend a typical weekend when you were young?',
    ['Who decided the plan?', 'What food or ritual marked it?', 'What would you recreate for your own family?'],
    'Family Life',
    ['home', 'traditions', 'childhood'],
    ['general', 'self', 'child', 'parent'],
  ),
  p(
    'vehicle-001',
    'Was there a car, bike or journey that belongs in your family story?',
    ['Where did it take you?', 'Who drove?', 'What happened on the road?'],
    'People, Places & Things',
    ['places', 'people, places & things'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'wedding-001',
    'What do you remember about your wedding day — or a wedding that shaped you?',
    ['Who was there that mattered most?', 'What small detail went wrong or right?', 'What feeling do you want preserved?'],
    'Love & Partnership',
    ['marriage', 'celebrations'],
    ['general', 'self', 'parent', 'memorial'],
  ),
  p(
    'birth-001',
    'What is the story of welcoming a child into the family?',
    ['Who was waiting?', 'What name story sits behind it?', 'What surprised you about that day?'],
    'Family Life',
    ['children', 'celebrations', 'first memories'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'mentor-001',
    'Who outside your family believed in you when you needed it?',
    ['What did they say or do?', 'How did your path change?', 'Have you done the same for someone else?'],
    'Work & Purpose',
    ['chosen family', 'lessons learned', 'work'],
    ['general', 'self', 'parent'],
  ),
  p(
    'kitchen-001',
    'What happened in the kitchen that never made it to a photograph?',
    ['Who was there?', 'What were you making or fixing?', 'What got spilled, burned or saved?'],
    'Family Life',
    ['food', 'home'],
    ['general', 'self', 'parent', 'grandparent', 'child'],
  ),
  p(
    'photo-001',
    'Choose a photo you love — real or in your mind — and tell the story behind it.',
    ['Who is in it?', 'What happened just before or after?', 'Why did someone take it?'],
    'Moments & Meaning',
    ['first memories', 'celebrations'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'weather-001',
    'What weather or season is tied to a memory you return to?',
    ['Where were you?', 'Who was with you?', 'What were you wearing or carrying?'],
    'Moments & Meaning',
    ['places', 'childhood'],
    ['general', 'self', 'parent', 'child'],
  ),
  p(
    'handwriting-001',
    'Is there a letter, card or note someone wrote that you still keep?',
    ['What did it say — even roughly?', 'When did you read it last?', 'What would you want kept with it?'],
    'People, Places & Things',
    ['favourite objects / keepsakes', 'love', 'memorial'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'routine-001',
    'What small daily routine made someone in your family feel cared for?',
    ['Morning or evening?', 'Who did it for whom?', 'Could it continue in a gentle new way?'],
    'Family Life',
    ['home', 'parents', 'traditions'],
    ['general', 'self', 'parent', 'child', 'memorial'],
  ),
  p(
    'mischief-001',
    'What harmless mischief did you get away with as a young person?',
    ['Who was your accomplice?', 'Did anyone find out later?', 'Why is it safe to laugh about now?'],
    'Growing Up',
    ['childhood', 'friendships'],
    ['general', 'self', 'child'],
  ),
  p(
    'legacy-001',
    'If someone opened your archive in fifty years, what is one story you hope they find first?',
    ['Who is it about?', 'What does it show about your values?', 'What feeling should it leave them with?'],
    'Reflections',
    ['lessons learned', 'proud moments'],
    ['general', 'self', 'parent', 'grandparent', 'memorial'],
  ),
  p(
    'self-001',
    'What would the younger you be surprised to learn about your life today?',
    ['What dream came true?', 'What dream changed shape?', 'What would you tell them to relax about?'],
    'Reflections',
    ['turning points', 'lessons learned'],
    ['self', 'general', 'parent'],
  ),
  p(
    'memorial-gentle-001',
    'What everyday habit or hobby made them unmistakably themselves?',
    ['When did family see it most?', 'What object or place goes with it?', 'What smile does it bring back?'],
    'Remembrance',
    ['memorial', 'people, places & things'],
    ['memorial', 'general', 'parent', 'grandparent'],
  ),
  p(
    'child-to-parent-001',
    'What do you wish you had asked a parent or grandparent while you still could?',
    ['What question would you start with?', 'What do you think they would have said?', 'What can you answer for the next generation instead?'],
    'Family Roots',
    ['parents', 'lessons learned', 'memorial'],
    ['general', 'self', 'child', 'parent', 'memorial'],
  ),
  p(
    'community-001',
    'What local shop, club or gathering place held your community together?',
    ['Who did you meet there?', 'What was sold, served or celebrated?', 'Is it still there?'],
    'People, Places & Things',
    ['places', 'chosen family'],
    ['general', 'self', 'parent', 'grandparent'],
  ),
  p(
    'bedtime-001',
    'What happened at bedtime in your childhood home?',
    ['Who read, prayed or tucked you in?', 'What sound meant lights out?', 'What comfort do you still associate with it?'],
    'The Early Years',
    ['childhood', 'home', 'parents'],
    ['general', 'self', 'child', 'parent'],
  ),
  p(
    'surprise-001',
    'What surprise — big or small — still makes you smile when you think of it?',
    ['Who planned it?', 'What was your first reaction?', 'Why does it belong in the archive?'],
    'Moments & Meaning',
    ['celebrations', 'love', 'proud moments'],
    ['general', 'self', 'parent', 'child', 'grandparent'],
  ),
];

export const START_FOR_ME_COPY = {
  label: 'Start for me',
  tagline: 'Not sure where to begin? Start with one memory.',
  todaysPrompt: "Today's memory prompt",
  suggestedChapter: 'Suggested chapter',
  followUps: 'Gentle follow-ups',
  writeNow: 'Write now',
  recordVoice: 'Record voice',
  saveForLater: 'Save for later',
  tryAnother: 'Try another prompt',
  savedTitle: 'Saved prompts',
  savedEmpty: 'Save prompts here when you want to come back later.',
  hubIntro: 'Get one memory prompt and choose whether to write, record or save it.',
};
